import type { CalendarPeriod } from '@ui/components/calendar/picker'
import { CalendarMode } from '@ui/components/calendar/picker'
import { capitalizeFirstLetter } from '@ui/utils/string'
import { formatDateByLang, isDateValid } from '@ui/utils/times'
import { isEqual, isSameDay, isSameMonth, isSameYear, parseISO } from 'date-fns'

interface Options {
  range?: boolean
  weekday?: boolean
  target?: CalendarMode
  locale?: string
}

function getDateFormat(target?: CalendarMode): string {
  if (target === CalendarMode.Days) {
    return 'd MMM yyyy'
  }
  if (target === CalendarMode.Times) {
    return 'eeeeee, d MMM, HH:mm'
  }
  return 'dd MMMM yyyy'
}

function getRangeFormats(start: Date, end: Date, target?: CalendarMode): [string, string] {
  const fullFormat = getDateFormat(target)

  if (target === CalendarMode.Days && isSameYear(start, end)) {
    return [isSameMonth(start, end) ? 'd' : 'd MMM', 'd MMM']
  }
  if (target === CalendarMode.Times && isSameDay(start, end)) {
    return [fullFormat, 'HH:mm']
  }
  return [fullFormat, fullFormat]
}

function formatSingleDate(date: Date, { target, locale, weekday }: Options): string {
  const text = formatDateByLang(date, getDateFormat(target), locale)
  if (weekday && target === CalendarMode.Days) {
    return `${capitalizeFirstLetter(formatDateByLang(date, 'eeeeee', locale))}, ${text}`
  }
  return text
}

function formatPeriod(start: Date, end: Date, { target, locale }: Options): string {
  if (isEqual(start, end)) {
    return formatSingleDate(start, { target, locale })
  }
  const [startFormat, endFormat] = getRangeFormats(start, end, target)
  return `${formatDateByLang(start, startFormat, locale)} — ${formatDateByLang(end, endFormat, locale)}`
}

function getSingleDateValue(modelValue: unknown) {
  if (typeof modelValue === 'string') {
    return parseISO(modelValue)
  }

  return modelValue instanceof Date ? modelValue : undefined
}

function isCalendarPeriod(modelValue: unknown): modelValue is CalendarPeriod {
  return (
    typeof modelValue === 'object'
    && modelValue !== null
    && 'start' in modelValue
    && modelValue.start instanceof Date
    && 'end' in modelValue
    && modelValue.end instanceof Date
  )
}

export function getDateInputText(modelValue?: unknown, options: Options = {}): string {
  const singleDate = getSingleDateValue(modelValue)
  if (!options.range && singleDate && isDateValid(singleDate)) {
    return formatSingleDate(singleDate, options)
  }

  if (isCalendarPeriod(modelValue)) {
    const { start, end } = modelValue
    if (start && end && isDateValid(start) && isDateValid(end)) {
      return formatPeriod(start, end, options)
    }
  }
  return ''
}
